import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { speakText } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './WordBank.css';
import ProfileMenu from '../ProfileMenu';
import FlashcardSession from './FlashcardSession';


const TEXT = {
    en: {
        wbTitle: 'Word bank',
        addWord: '+ Add word',
        searchPlaceholder: 'Search your saved words...',
        saved: 'saved',
        mastered: 'mastered',
        learning: 'learning',
        newLabel: 'new',
        fAll: 'All words',
        fNew: 'New',
        fLearning: 'Learning',
        fStrong: 'Strong',
        fLesson: 'From lessons',
        fLuna: 'From Gabi',
        fStory: 'From stories',
        practiceAll: 'Practise all →',
        loading: 'Loading your words...',
        emptyNone: 'No words saved yet',
        emptyNotFound: 'No words found',
        emptyNoneDesc: 'Complete a lesson to start building your word bank, or add a word manually',
        emptyNotFoundDesc: 'Try a different search or filter',
        mNew: 'New',
        mLearning: 'Learning',
        mGetting: 'Getting there',
        mStrong: 'Strong',
        sLesson: 'Lesson',
        sStory: 'Story',
        sLuna: 'Gabi',
        sGrammar: 'Grammar',
        sAdded: 'Added',
        deleteWordTitle: 'Delete word',
        pronunciation: 'Pronunciation',
        note: 'Note',
        examples: 'Example sentences',
        hearSentence: 'Hear sentence',
        mastery: 'Mastery',
        flashcard: 'Flashcard',
        useInSentence: 'Use in sentence →',
        titleRelated: 'You already have a related word',
        titleConjugated: 'This is a conjugated verb',
        titleDidYouMean: 'Did you mean...',
        descRelatedBase: 'You already have {related} (the base verb) in your Word Bank. "{typed}" is a conjugated form of the same verb. Do you still want to save it separately?',
        descRelatedForm: 'You already have {related} (a conjugated form) in your Word Bank. Do you still want to save the base verb "{base}" separately?',
        descConjugated: '"{typed}" is the {info} form of the verb {baseForm} ({translation}). It’s usually more useful to learn the base verb.',
        descDidYouMean: '"{typed}" isn’t quite right. Did you mean {suggestion} ({translation})?',
        yesSaveAnyway: 'Yes, save "{word}" anyway →',
        noHaveIt: 'No, I already have it',
        yesSave: 'Yes, save "{word}" →',
        noSaveAsTyped: 'No, save "{word}" as I typed it',
        addTitle: 'Add a word',
        addDesc: 'Type any {lang} word or phrase. Vozoly checks the spelling and adds the translation, pronunciation and an example for you.',
        addPlaceholder: 'Type a word in {lang}...',
        cancel: 'Cancel',
        addBtn: 'Add word →',
        checking: 'Checking...',
        errDuplicate: '"{word}" is already in your Word Bank.',
        errUnknown: 'We couldn’t recognize that word. Check the spelling and try again.',
        errLookup: 'Word lookup is temporarily unavailable. The word has been saved without details. You can delete it and add it again later to get the full definition.',
        deleteTitle: 'Delete this word?',
        deleteDesc: 'This will permanently remove it from your word bank.',
        deleteBtn: 'Delete',
        sentenceTitle: 'Use "{word}" in a sentence',
        sentenceDesc: 'Write a sentence in {lang} using this word ({translation}).',
        thisWord: 'this word',
        sentencePlaceholder: 'Write a sentence...',
        close: 'Close',
        checkSentence: 'Check sentence →',
        sentenceUnavailable: 'Sentence checking is temporarily unavailable. Please try again later.',
        tryCorrected: 'Try: "{sentence}"',
        noteEncountered: 'You encountered "{typed}", the {info} form of this verb.',
        noteFormOf: '{info} form of "{base}".',
        noteConjugatedOf: 'Conjugated form of "{base}".',
        navHome: 'Home',
        navTopics: 'Topics',
        navGrammar: 'Grammar',
        navWordBank: 'Word bank',
        navGames: 'Games',
    },
    it: {
        wbTitle: 'Word bank',
        addWord: '+ Aggiungi parola',
        searchPlaceholder: 'Cerca tra le parole salvate...',
        saved: 'salvate',
        mastered: 'padroneggiate',
        learning: 'in corso',
        newLabel: 'nuove',
        fAll: 'Tutte le parole',
        fNew: 'Nuove',
        fLearning: 'In corso',
        fStrong: 'Solide',
        fLesson: 'Dalle lezioni',
        fLuna: 'Da Gabi',
        fStory: 'Dalle storie',
        practiceAll: 'Esercitati su tutte →',
        loading: 'Caricamento delle tue parole...',
        emptyNone: 'Ancora nessuna parola salvata',
        emptyNotFound: 'Nessuna parola trovata',
        emptyNoneDesc: 'Completa una lezione per iniziare a costruire il tuo Word Bank, oppure aggiungi una parola a mano',
        emptyNotFoundDesc: 'Prova con un’altra ricerca o un altro filtro',
        mNew: 'Nuova',
        mLearning: 'In corso',
        mGetting: 'Quasi lì',
        mStrong: 'Solida',
        sLesson: 'Lezione',
        sStory: 'Storia',
        sLuna: 'Gabi',
        sGrammar: 'Grammatica',
        sAdded: 'Aggiunta',
        deleteWordTitle: 'Elimina parola',
        pronunciation: 'Pronuncia',
        note: 'Nota',
        examples: 'Frasi di esempio',
        hearSentence: 'Ascolta la frase',
        mastery: 'Padronanza',
        flashcard: 'Flashcard',
        useInSentence: 'Usa in una frase →',
        titleRelated: 'Hai già una parola collegata',
        titleConjugated: 'Questo è un verbo coniugato',
        titleDidYouMean: 'Intendevi...',
        descRelatedBase: 'Hai già {related} (il verbo base) nel tuo Word Bank. "{typed}" è una forma coniugata dello stesso verbo. Vuoi comunque salvarla separatamente?',
        descRelatedForm: 'Hai già {related} (una forma coniugata) nel tuo Word Bank. Vuoi comunque salvare separatamente il verbo base "{base}"?',
        descConjugated: '"{typed}" è la forma {info} del verbo {baseForm} ({translation}). Di solito è più utile imparare il verbo base.',
        descDidYouMean: '"{typed}" non è del tutto corretto. Intendevi {suggestion} ({translation})?',
        yesSaveAnyway: 'Sì, salva comunque "{word}" →',
        noHaveIt: 'No, ce l’ho già',
        yesSave: 'Sì, salva "{word}" →',
        noSaveAsTyped: 'No, salva "{word}" come l’ho scritta',
        addTitle: 'Aggiungi una parola',
        addDesc: 'Scrivi una qualsiasi parola o frase in {lang}. Vozoly controlla l’ortografia e aggiunge traduzione, pronuncia ed esempio per te.',
        addPlaceholder: 'Scrivi una parola in {lang}...',
        cancel: 'Annulla',
        addBtn: 'Aggiungi parola →',
        checking: 'Controllo...',
        errDuplicate: '"{word}" è già nel tuo Word Bank.',
        errUnknown: 'Non siamo riusciti a riconoscere quella parola. Controlla l’ortografia e riprova.',
        errLookup: 'La ricerca delle parole non è disponibile al momento. La parola è stata salvata senza dettagli. Puoi eliminarla e riaggiungerla più tardi per avere la definizione completa.',
        deleteTitle: 'Eliminare questa parola?',
        deleteDesc: 'Verrà rimossa definitivamente dal tuo Word Bank.',
        deleteBtn: 'Elimina',
        sentenceTitle: 'Usa "{word}" in una frase',
        sentenceDesc: 'Scrivi una frase in {lang} usando questa parola ({translation}).',
        thisWord: 'questa parola',
        sentencePlaceholder: 'Scrivi una frase...',
        close: 'Chiudi',
        checkSentence: 'Controlla la frase →',
        sentenceUnavailable: 'Il controllo delle frasi non è disponibile al momento. Riprova più tardi.',
        tryCorrected: 'Prova: "{sentence}"',
        noteEncountered: 'Hai incontrato "{typed}", la forma {info} di questo verbo.',
        noteFormOf: 'Forma {info} di "{base}".',
        noteConjugatedOf: 'Forma coniugata di "{base}".',
        navHome: 'Home',
        navTopics: 'Argomenti',
        navGrammar: 'Grammatica',
        navWordBank: 'Word bank',
        navGames: 'Giochi',
    },
    es: {
        wbTitle: 'Word bank',
        addWord: '+ Añadir palabra',
        searchPlaceholder: 'Busca entre tus palabras guardadas...',
        saved: 'guardadas',
        mastered: 'dominadas',
        learning: 'aprendiendo',
        newLabel: 'nuevas',
        fAll: 'Todas las palabras',
        fNew: 'Nuevas',
        fLearning: 'Aprendiendo',
        fStrong: 'Sólidas',
        fLesson: 'De las lecciones',
        fLuna: 'De Gabi',
        fStory: 'De las historias',
        practiceAll: 'Practicar todas →',
        loading: 'Cargando tus palabras...',
        emptyNone: 'Todavía no hay palabras guardadas',
        emptyNotFound: 'No se encontraron palabras',
        emptyNoneDesc: 'Completa una lección para empezar a crear tu Word Bank, o añade una palabra a mano',
        emptyNotFoundDesc: 'Prueba con otra búsqueda u otro filtro',
        mNew: 'Nueva',
        mLearning: 'Aprendiendo',
        mGetting: 'Casi lo logras',
        mStrong: 'Sólida',
        sLesson: 'Lección',
        sStory: 'Historia',
        sLuna: 'Gabi',
        sGrammar: 'Gramática',
        sAdded: 'Añadida',
        deleteWordTitle: 'Eliminar palabra',
        pronunciation: 'Pronunciación',
        note: 'Nota',
        examples: 'Frases de ejemplo',
        hearSentence: 'Escuchar la frase',
        mastery: 'Dominio',
        flashcard: 'Tarjeta',
        useInSentence: 'Usar en una frase →',
        titleRelated: 'Ya tienes una palabra relacionada',
        titleConjugated: 'Este es un verbo conjugado',
        titleDidYouMean: '¿Quisiste decir...?',
        descRelatedBase: 'Ya tienes {related} (el verbo base) en tu Word Bank. "{typed}" es una forma conjugada del mismo verbo. ¿Quieres guardarla por separado de todos modos?',
        descRelatedForm: 'Ya tienes {related} (una forma conjugada) en tu Word Bank. ¿Quieres guardar por separado el verbo base "{base}" de todos modos?',
        descConjugated: '"{typed}" es la forma {info} del verbo {baseForm} ({translation}). Normalmente es más útil aprender el verbo base.',
        descDidYouMean: '"{typed}" no es del todo correcto. ¿Quisiste decir {suggestion} ({translation})?',
        yesSaveAnyway: 'Sí, guardar "{word}" de todos modos →',
        noHaveIt: 'No, ya la tengo',
        yesSave: 'Sí, guardar "{word}" →',
        noSaveAsTyped: 'No, guardar "{word}" como la escribí',
        addTitle: 'Añadir una palabra',
        addDesc: 'Escribe cualquier palabra o frase en {lang}. Vozoly revisa la ortografía y añade la traducción, la pronunciación y un ejemplo por ti.',
        addPlaceholder: 'Escribe una palabra en {lang}...',
        cancel: 'Cancelar',
        addBtn: 'Añadir palabra →',
        checking: 'Comprobando...',
        errDuplicate: '"{word}" ya está en tu Word Bank.',
        errUnknown: 'No pudimos reconocer esa palabra. Revisa la ortografía e inténtalo de nuevo.',
        errLookup: 'La búsqueda de palabras no está disponible por ahora. La palabra se guardó sin detalles. Puedes eliminarla y volver a añadirla más tarde para obtener la definición completa.',
        deleteTitle: '¿Eliminar esta palabra?',
        deleteDesc: 'Se quitará de tu Word Bank de forma permanente.',
        deleteBtn: 'Eliminar',
        sentenceTitle: 'Usa "{word}" en una frase',
        sentenceDesc: 'Escribe una frase en {lang} con esta palabra ({translation}).',
        thisWord: 'esta palabra',
        sentencePlaceholder: 'Escribe una frase...',
        close: 'Cerrar',
        checkSentence: 'Comprobar la frase →',
        sentenceUnavailable: 'La revisión de frases no está disponible por ahora. Inténtalo más tarde.',
        tryCorrected: 'Prueba: "{sentence}"',
        noteEncountered: 'Te encontraste con "{typed}", la forma {info} de este verbo.',
        noteFormOf: 'Forma {info} de "{base}".',
        noteConjugatedOf: 'Forma conjugada de "{base}".',
        navHome: 'Inicio',
        navTopics: 'Temas',
        navGrammar: 'Gramática',
        navWordBank: 'Word bank',
        navGames: 'Juegos',
    },
    fr: {
        wbTitle: 'Word bank',
        addWord: '+ Ajouter un mot',
        searchPlaceholder: 'Cherche parmi tes mots enregistrés...',
        saved: 'enregistrés',
        mastered: 'maîtrisés',
        learning: 'en cours',
        newLabel: 'nouveaux',
        fAll: 'Tous les mots',
        fNew: 'Nouveaux',
        fLearning: 'En cours',
        fStrong: 'Solides',
        fLesson: 'Des leçons',
        fLuna: 'De Gabi',
        fStory: 'Des histoires',
        practiceAll: 'Tout réviser →',
        loading: 'Chargement de tes mots...',
        emptyNone: 'Aucun mot enregistré pour l’instant',
        emptyNotFound: 'Aucun mot trouvé',
        emptyNoneDesc: 'Termine une leçon pour commencer ton Word Bank, ou ajoute un mot à la main',
        emptyNotFoundDesc: 'Essaie une autre recherche ou un autre filtre',
        mNew: 'Nouveau',
        mLearning: 'En cours',
        mGetting: 'Bientôt',
        mStrong: 'Solide',
        sLesson: 'Leçon',
        sStory: 'Histoire',
        sLuna: 'Gabi',
        sGrammar: 'Grammaire',
        sAdded: 'Ajouté',
        deleteWordTitle: 'Supprimer le mot',
        pronunciation: 'Prononciation',
        note: 'Note',
        examples: 'Phrases d’exemple',
        hearSentence: 'Écouter la phrase',
        mastery: 'Maîtrise',
        flashcard: 'Carte',
        useInSentence: 'Utiliser dans une phrase →',
        titleRelated: 'Tu as déjà un mot lié',
        titleConjugated: 'C’est un verbe conjugué',
        titleDidYouMean: 'Voulais-tu dire...',
        descRelatedBase: 'Tu as déjà {related} (le verbe de base) dans ton Word Bank. "{typed}" est une forme conjuguée du même verbe. Veux-tu quand même l’enregistrer séparément ?',
        descRelatedForm: 'Tu as déjà {related} (une forme conjuguée) dans ton Word Bank. Veux-tu quand même enregistrer séparément le verbe de base "{base}" ?',
        descConjugated: '"{typed}" est la forme {info} du verbe {baseForm} ({translation}). Il est généralement plus utile d’apprendre le verbe de base.',
        descDidYouMean: '"{typed}" n’est pas tout à fait correct. Voulais-tu dire {suggestion} ({translation}) ?',
        yesSaveAnyway: 'Oui, enregistrer quand même "{word}" →',
        noHaveIt: 'Non, je l’ai déjà',
        yesSave: 'Oui, enregistrer "{word}" →',
        noSaveAsTyped: 'Non, enregistrer "{word}" tel que je l’ai écrit',
        addTitle: 'Ajouter un mot',
        addDesc: 'Écris n’importe quel mot ou phrase en {lang}. Vozoly vérifie l’orthographe et ajoute la traduction, la prononciation et un exemple pour toi.',
        addPlaceholder: 'Écris un mot en {lang}...',
        cancel: 'Annuler',
        addBtn: 'Ajouter le mot →',
        checking: 'Vérification...',
        errDuplicate: '"{word}" est déjà dans ton Word Bank.',
        errUnknown: 'Nous n’avons pas reconnu ce mot. Vérifie l’orthographe et réessaie.',
        errLookup: 'La recherche de mots est temporairement indisponible. Le mot a été enregistré sans détails. Tu peux le supprimer et le rajouter plus tard pour obtenir la définition complète.',
        deleteTitle: 'Supprimer ce mot ?',
        deleteDesc: 'Il sera définitivement retiré de ton Word Bank.',
        deleteBtn: 'Supprimer',
        sentenceTitle: 'Utilise "{word}" dans une phrase',
        sentenceDesc: 'Écris une phrase en {lang} avec ce mot ({translation}).',
        thisWord: 'ce mot',
        sentencePlaceholder: 'Écris une phrase...',
        close: 'Fermer',
        checkSentence: 'Vérifier la phrase →',
        sentenceUnavailable: 'La vérification des phrases est temporairement indisponible. Réessaie plus tard.',
        tryCorrected: 'Essaie : "{sentence}"',
        noteEncountered: 'Tu as rencontré "{typed}", la forme {info} de ce verbe.',
        noteFormOf: 'Forme {info} de "{base}".',
        noteConjugatedOf: 'Forme conjuguée de "{base}".',
        navHome: 'Accueil',
        navTopics: 'Thèmes',
        navGrammar: 'Grammaire',
        navWordBank: 'Word bank',
        navGames: 'Jeux',
    },
    pt: {
        wbTitle: 'Word bank',
        addWord: '+ Adicionar palavra',
        searchPlaceholder: 'Pesquise entre suas palavras salvas...',
        saved: 'salvas',
        mastered: 'dominadas',
        learning: 'aprendendo',
        newLabel: 'novas',
        fAll: 'Todas as palavras',
        fNew: 'Novas',
        fLearning: 'Aprendendo',
        fStrong: 'Sólidas',
        fLesson: 'Das lições',
        fLuna: 'Da Gabi',
        fStory: 'Das histórias',
        practiceAll: 'Praticar todas →',
        loading: 'Carregando suas palavras...',
        emptyNone: 'Nenhuma palavra salva ainda',
        emptyNotFound: 'Nenhuma palavra encontrada',
        emptyNoneDesc: 'Conclua uma lição para começar seu Word Bank, ou adicione uma palavra manualmente',
        emptyNotFoundDesc: 'Tente outra busca ou outro filtro',
        mNew: 'Nova',
        mLearning: 'Aprendendo',
        mGetting: 'Quase lá',
        mStrong: 'Sólida',
        sLesson: 'Lição',
        sStory: 'História',
        sLuna: 'Gabi',
        sGrammar: 'Gramática',
        sAdded: 'Adicionada',
        deleteWordTitle: 'Excluir palavra',
        pronunciation: 'Pronúncia',
        note: 'Nota',
        examples: 'Frases de exemplo',
        hearSentence: 'Ouvir a frase',
        mastery: 'Domínio',
        flashcard: 'Cartão',
        useInSentence: 'Usar em uma frase →',
        titleRelated: 'Você já tem uma palavra relacionada',
        titleConjugated: 'Este é um verbo conjugado',
        titleDidYouMean: 'Você quis dizer...',
        descRelatedBase: 'Você já tem {related} (o verbo base) no seu Word Bank. "{typed}" é uma forma conjugada do mesmo verbo. Ainda quer salvá-la separadamente?',
        descRelatedForm: 'Você já tem {related} (uma forma conjugada) no seu Word Bank. Ainda quer salvar separadamente o verbo base "{base}"?',
        descConjugated: '"{typed}" é a forma {info} do verbo {baseForm} ({translation}). Geralmente é mais útil aprender o verbo base.',
        descDidYouMean: '"{typed}" não está bem certo. Você quis dizer {suggestion} ({translation})?',
        yesSaveAnyway: 'Sim, salvar "{word}" mesmo assim →',
        noHaveIt: 'Não, eu já tenho',
        yesSave: 'Sim, salvar "{word}" →',
        noSaveAsTyped: 'Não, salvar "{word}" como eu digitei',
        addTitle: 'Adicionar uma palavra',
        addDesc: 'Digite qualquer palavra ou frase em {lang}. O Vozoly confere a ortografia e adiciona a tradução, a pronúncia e um exemplo para você.',
        addPlaceholder: 'Digite uma palavra em {lang}...',
        cancel: 'Cancelar',
        addBtn: 'Adicionar palavra →',
        checking: 'Verificando...',
        errDuplicate: '"{word}" já está no seu Word Bank.',
        errUnknown: 'Não conseguimos reconhecer essa palavra. Confira a ortografia e tente de novo.',
        errLookup: 'A busca de palavras está temporariamente indisponível. A palavra foi salva sem detalhes. Você pode excluí-la e adicioná-la de novo mais tarde para ter a definição completa.',
        deleteTitle: 'Excluir esta palavra?',
        deleteDesc: 'Ela será removida do seu Word Bank de forma permanente.',
        deleteBtn: 'Excluir',
        sentenceTitle: 'Use "{word}" em uma frase',
        sentenceDesc: 'Escreva uma frase em {lang} usando esta palavra ({translation}).',
        thisWord: 'esta palavra',
        sentencePlaceholder: 'Escreva uma frase...',
        close: 'Fechar',
        checkSentence: 'Verificar a frase →',
        sentenceUnavailable: 'A verificação de frases está temporariamente indisponível. Tente novamente mais tarde.',
        tryCorrected: 'Tente: "{sentence}"',
        noteEncountered: 'Você encontrou "{typed}", a forma {info} deste verbo.',
        noteFormOf: 'Forma {info} de "{base}".',
        noteConjugatedOf: 'Forma conjugada de "{base}".',
        navHome: 'Início',
        navTopics: 'Temas',
        navGrammar: 'Gramática',
        navWordBank: 'Word bank',
        navGames: 'Jogos',
    },
};

const LANGUAGE_NAMES = {
    en: { english: 'English', italian: 'Italian', spanish: 'Spanish', french: 'French', portuguese: 'Portuguese' },
    it: { english: 'inglese', italian: 'italiano', spanish: 'spagnolo', french: 'francese', portuguese: 'portoghese' },
    es: { english: 'inglés', italian: 'italiano', spanish: 'español', french: 'francés', portuguese: 'portugués' },
    fr: { english: 'anglais', italian: 'italien', spanish: 'espagnol', french: 'français', portuguese: 'portugais' },
    pt: { english: 'inglês', italian: 'italiano', spanish: 'espanhol', french: 'francês', portuguese: 'português' },
};

const getMasteryLabel = (mastery) => {
    if (mastery === 0) return { labelKey: 'mNew', color: '#6B7280', bg: '#F3F4F6' };
    if (mastery < 40) return { labelKey: 'mLearning', color: '#A32D2D', bg: '#FCEBEB' };
    if (mastery < 70) return { labelKey: 'mGetting', color: '#633806', bg: '#FAEEDA' };
    return { labelKey: 'mStrong', color: '#085041', bg: '#E1F5EE' };
};

const getSourceStyle = (source) => {
    switch (source) {
        case 'lesson': return { bg: '#E1F5EE', color: '#085041', labelKey: 'sLesson' };
        case 'story': return { bg: '#EEEDFE', color: '#3C3489', labelKey: 'sStory' };
        case 'luna': return { bg: '#E6F1FB', color: '#0C447C', labelKey: 'sLuna' };
        case 'grammar': return { bg: '#FAEEDA', color: '#633806', labelKey: 'sGrammar' };
        default: return { bg: '#F3F4F6', color: '#6B7280', labelKey: 'sAdded' };
    }
};

function WordBank() {
    const navigate = useNavigate();
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage } = useUserLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const apiLangName = targetLang.charAt(0).toUpperCase() + targetLang.slice(1);
    const langName = LANGUAGE_NAMES[uiLang][targetLang] || apiLangName;
    const s = (key, values) => {
        let text = TEXT[uiLang][key] || TEXT.en[key];
        Object.keys(values || {}).forEach(name => {
            text = text.replace(`{${name}}`, values[name]);
        });
        return text;
    };
    // Like s(), but the names listed in "bold" are shown in bold
    const rich = (key, values, bold) => {
        const text = TEXT[uiLang][key] || TEXT.en[key];
        return text.split(/(\{[a-zA-Z]+\})/).map((part, i) => {
            const match = part.match(/^\{([a-zA-Z]+)\}$/);
            if (!match) return part;
            const value = values[match[1]];
            return (bold || []).includes(match[1]) ? <strong key={i}>{value}</strong> : value;
        });
    };
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [expandedId, setExpandedId] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const displayName = localStorage.getItem('vozoly_display_name') || 'U';
    const userInitial = displayName[0].toUpperCase();
    const [newWord, setNewWord] = useState('');
    const [words, setWords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [flashcardMode, setFlashcardMode] = useState(false);
    const [sentenceModalWord, setSentenceModalWord] = useState(null);
    const [sentenceInput, setSentenceInput] = useState('');
    const [sentenceChecking, setSentenceChecking] = useState(false);
    const [sentenceResult, setSentenceResult] = useState(null);

    const loadWords = useCallback(async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
            setLoading(false);
            return;
        }
        const { data } = await supabase
            .from('word_bank')
            .select('*')
            .eq('user_id', user.id)
            .eq('target_language', targetLang)
            .order('created_at', { ascending: false });
        if (data) {
            setWords(data.map(w => ({
                id: w.id,
                word: w.word,
                translation: w.translation || '',
                type: w.type || '',
                phonetic: w.phonetic || '',
                note: w.note || '',
                source: w.source || 'manual',
                mastery: w.mastery || 0,
                examples: w.examples || [],
            })));
        }
        setLoading(false);
    }, [targetLang]);

    useEffect(() => {
        loadWords();
    }, [loadWords]);

    const updateMastery = async (id, delta) => {
        const word = words.find(w => w.id === id);
        if (!word) return;
        const newMastery = Math.max(0, Math.min(100, word.mastery + delta));
        await supabase.from('word_bank').update({ mastery: newMastery }).eq('id', id);
        setWords(prev => prev.map(w => w.id === id ? { ...w, mastery: newMastery } : w));
    };

    const handleFlashcardAnswer = (id, knew) => {
        updateMastery(id, knew ? 25 : -15);
    };

    const checkSentence = async () => {
        if (!sentenceInput.trim() || !sentenceModalWord) return;
        setSentenceChecking(true);
        try {
            const response = await fetch('/api/check-sentence', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    word: sentenceModalWord.word,
                    translation: sentenceModalWord.translation,
                    sentence: sentenceInput.trim(),
                    language: apiLangName,
                }),
            });
            if (!response.ok) throw new Error('API unavailable');
            const result = await response.json();
            setSentenceResult(result);
            setSentenceChecking(false);
            if (result.correct) {
                await updateMastery(sentenceModalWord.id, 15);
            }
        } catch (err) {
            setSentenceChecking(false);
            setSentenceResult({ correct: false, feedback: s('sentenceUnavailable'), correctedSentence: null });
        }
    };

    const [deleteConfirmId, setDeleteConfirmId] = useState(null);

    const confirmDeleteWord = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || !deleteConfirmId) return;
        await supabase.from('word_bank').delete().eq('id', deleteConfirmId).eq('user_id', user.id);
        setWords(prev => prev.filter(w => w.id !== deleteConfirmId));
        if (expandedId === deleteConfirmId) setExpandedId(null);
        setDeleteConfirmId(null);
    };

    const filters = [
        { id: 'all', label: s('fAll') },
        { id: 'new', label: s('fNew') },
        { id: 'learning', label: s('fLearning') },
        { id: 'strong', label: s('fStrong') },
        { id: 'lesson', label: s('fLesson') },
        { id: 'luna', label: s('fLuna') },
        { id: 'story', label: s('fStory') },
    ];

    const filteredWords = words.filter(word => {
        const matchesSearch =
            word.word.toLowerCase().includes(search.toLowerCase()) ||
            word.translation.toLowerCase().includes(search.toLowerCase());

        if (!matchesSearch) return false;

        if (filter === 'all') return true;
        if (filter === 'new') return word.mastery === 0;
        if (filter === 'learning') return word.mastery > 0 && word.mastery < 70;
        if (filter === 'strong') return word.mastery >= 70;
        if (filter === 'lesson') return word.source === 'lesson';
        if (filter === 'luna') return word.source === 'luna';
        if (filter === 'story') return word.source === 'story';
        return true;
    });

    const totalWords = words.length;
    const masteredWords = words.filter(w => w.mastery >= 70).length;
    const learningWords = words.filter(w => w.mastery > 0 && w.mastery < 70).length;
    const newWords = words.filter(w => w.mastery === 0).length;

    const [checkingWord, setCheckingWord] = useState(false);
    const [wordError, setWordError] = useState('');
    const [wordSuggestion, setWordSuggestion] = useState(null);

    const closeAddModal = () => {
        setShowAddModal(false);
        setNewWord('');
        setWordSuggestion(null);
        setWordError('');
    };

    const saveEnrichedWord = async (result, originalTyped) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const finalWord = result.wordToSave || originalTyped;
        const entryType = finalWord.trim().split(/\s+/).length > 1 ? 'phrase' : 'word';
        let finalNote = result.note || '';
        if (result.isConjugated) {
            const conjugationNote = s('noteEncountered', { typed: result.typedWord, info: result.conjugationInfo });
            finalNote = finalNote ? `${conjugationNote} ${finalNote}` : conjugationNote;
        }
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word: finalWord,
            translation: result.translation,
            phonetic: result.phonetic,
            type: result.type,
            note: finalNote,
            examples: result.examples || [],
            entry_type: entryType,
            source: 'manual',
            original_input: result.typedWord !== finalWord ? result.typedWord : null,
            target_language: targetLang,
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        closeAddModal();
        setLoading(true);
        await loadWords();
    };

    const saveRawWord = async (word) => {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        const entryType = word.trim().split(/\s+/).length > 1 ? 'phrase' : 'word';
        await supabase.from('word_bank').upsert({
            user_id: user.id,
            word,
            source: 'manual',
            entry_type: entryType,
            target_language: targetLang,
        }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
        closeAddModal();
        setLoading(true);
        await loadWords();
    };

    const checkAndAddWord = async () => {
        if (!newWord.trim()) return;
        setWordError('');

        const exactDuplicate = words.find(w =>
            w.word.toLowerCase() === newWord.trim().toLowerCase()
        );
        if (exactDuplicate) {
            setWordError(s('errDuplicate', { word: exactDuplicate.word }));
            return;
        }

        setCheckingWord(true);
        try {
            const response = await fetch('/api/identify-word', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ word: newWord.trim(), language: apiLangName, interfaceLanguage: uiLang }),
            });
            if (!response.ok) throw new Error('API unavailable');
            const result = await response.json();
            setCheckingWord(false);

            if (!result.valid) {
                setWordError(s('errUnknown'));
                return;
            }

            const baseForm = result.wordToSave?.toLowerCase();
            const typedLower = result.typedWord?.toLowerCase();

            const relatedMatch = words.find(w => {
                const wLower = w.word.toLowerCase();
                return wLower === baseForm || wLower === typedLower ||
                    (baseForm && wLower.startsWith(baseForm.slice(0, Math.max(4, baseForm.length - 3))));
            });

            if (relatedMatch && relatedMatch.word.toLowerCase() !== newWord.trim().toLowerCase()) {
                const isMatchBase = relatedMatch.word.toLowerCase() === baseForm;
                setWordError('');
                setWordSuggestion({
                    ...result,
                    relatedWord: relatedMatch.word,
                    relatedIsBase: isMatchBase,
                });
                return;
            }

            if (result.wasCorrected || result.isConjugated) {
                setWordSuggestion(result);
            } else {
                await saveEnrichedWord(result, newWord.trim());
            }
        } catch (err) {
            setCheckingWord(false);
            setWordError(s('errLookup'));
            await saveRawWord(newWord.trim());
        }
    };

    return (
        <div className="wb-screen">

            <div className="wb-top-bar">
                <div className="wb-top-row">
                    <div className="wb-title">{s('wbTitle')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <button className="wb-add-btn" onClick={() => setShowAddModal(true)}>
                            {s('addWord')}
                        </button>
                        <ProfileMenu initial={userInitial} />
                    </div>
                </div>
                <div className="wb-search">
                    <span className="wb-search-icon">🔍</span>
                    <input
                        className="wb-search-input"
                        type="text"
                        placeholder={s('searchPlaceholder')}
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    {search && (
                        <button className="wb-search-clear" onClick={() => setSearch('')}>✕</button>
                    )}
                </div>
            </div>

            <div className="wb-stats-row">
                <div className="wb-stat">
                    <div className="wb-stat-value">{totalWords}</div>
                    <div className="wb-stat-label">{s('saved')}</div>
                </div>
                <div className="wb-stat">
                    <div className="wb-stat-value green">{masteredWords}</div>
                    <div className="wb-stat-label">{s('mastered')}</div>
                </div>
                <div className="wb-stat">
                    <div className="wb-stat-value amber">{learningWords}</div>
                    <div className="wb-stat-label">{s('learning')}</div>
                </div>
                <div className="wb-stat">
                    <div className="wb-stat-value">{newWords}</div>
                    <div className="wb-stat-label">{s('newLabel')}</div>
                </div>
            </div>

            <div className="wb-filters">
                {filters.map(f => (
                    <button
                        key={f.id}
                        className={`wb-filter-btn ${filter === f.id ? 'active' : ''}`}
                        onClick={() => setFilter(f.id)}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            <div className="wb-section-header">
                <div className="wb-section-label">
                    {filter === 'all' ? s('fAll') : filters.find(f => f.id === filter)?.label}
                    {' '}· {filteredWords.length}
                </div>
                <div className="wb-practice-all" onClick={() => setFlashcardMode(true)} style={{ cursor: 'pointer' }}>{s('practiceAll')}</div>
            </div>

            <div className="wb-word-list">
                {loading ? (
                    <div className="wb-empty">
                        <div className="wb-empty-icon">⏳</div>
                        <div className="wb-empty-title">{s('loading')}</div>
                    </div>
                ) : filteredWords.length === 0 ? (
                    <div className="wb-empty">
                        <div className="wb-empty-icon">📒</div>
                        <div className="wb-empty-title">
                            {words.length === 0 ? s('emptyNone') : s('emptyNotFound')}
                        </div>
                        <div className="wb-empty-desc">
                            {words.length === 0 ? s('emptyNoneDesc') : s('emptyNotFoundDesc')}
                        </div>
                    </div>
                ) : (
                    filteredWords.map(word => {
                        const mastery = getMasteryLabel(word.mastery);
                        const source = getSourceStyle(word.source);
                        const isExpanded = expandedId === word.id;

                        return (
                            <div
                                key={word.id}
                                className={`wb-word-card ${isExpanded ? 'expanded' : ''}`}
                            >
                                <div
                                    className="wb-word-top"
                                    onClick={() => setExpandedId(isExpanded ? null : word.id)}
                                >
                                    <div
                                        className="wb-mastery-ring"
                                        style={{ background: mastery.bg, color: mastery.color }}
                                    >
                                        {s(mastery.labelKey)[0]}
                                    </div>
                                    <div className="wb-word-info">
                                        <div className="wb-word-text">{word.word}</div>
                                        <div className="wb-word-translation">
                                            {word.translation}{word.type ? ` · ${word.type}` : ''}
                                        </div>
                                    </div>
                                    <div className="wb-word-meta">
                                        <span
                                            className="wb-source-tag"
                                            style={{ background: source.bg, color: source.color }}
                                        >
                                            {s(source.labelKey)}
                                        </span>
                                        <button
                                            className="wb-delete-btn"
                                            onClick={(e) => { e.stopPropagation(); setDeleteConfirmId(word.id); }}
                                            title={s('deleteWordTitle')}
                                        >
                                            🗑️
                                        </button>
                                        <span className="wb-chevron">{isExpanded ? '▲' : '▾'}</span>
                                    </div>
                                </div>

                                {isExpanded && (
                                    <div className="wb-word-detail">
                                        {word.phonetic && (
                                            <div className="wb-detail-section">
                                                <div className="wb-detail-label">{s('pronunciation')}</div>
                                                <div className="wb-pronunciation-row">
                                                    <div className="wb-phonetic">{word.phonetic}</div>
                                                    <button className="wb-play-btn" onClick={() => speakText(word.word, undefined, targetLang)}>▶</button>
                                                </div>
                                            </div>
                                        )}

                                        {word.note && (
                                            <div className="wb-detail-section">
                                                <div className="wb-detail-label">{s('note')}</div>
                                                <div className="wb-example-en">{word.note}</div>
                                            </div>
                                        )}

                                        {word.examples && word.examples.length > 0 && (
                                            <div className="wb-detail-section">
                                                <div className="wb-detail-label">{s('examples')}</div>
                                                {word.examples.map((ex, i) => (
                                                    <div key={i} className="wb-example">
                                                        <div className="wb-example-it-row">
                                                            <div className="wb-example-it">
                                                                {ex.it.split(word.word).map((part, j, arr) => (
                                                                    <span key={j}>
                                                                        {part}
                                                                        {j < arr.length - 1 && (
                                                                            <span className="wb-example-highlight">{word.word}</span>
                                                                        )}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                            <button
                                                                className="wb-example-speak-btn"
                                                                onClick={() => speakText(ex.it, undefined, targetLang)}
                                                                title={s('hearSentence')}
                                                            >
                                                                🔊
                                                            </button>
                                                        </div>
                                                        <div className="wb-example-en">{ex.en}</div>
                                                    </div>
                                                ))}
                                            </div>
                                        )}

                                        <div className="wb-detail-section">
                                            <div className="wb-detail-label">{s('mastery')}</div>
                                            <div className="wb-mastery-wrap">
                                                <div className="wb-mastery-info">
                                                    <span style={{ color: mastery.color, fontWeight: 500 }}>
                                                        {s(mastery.labelKey)}
                                                    </span>
                                                    <span className="wb-mastery-pct">{word.mastery}%</span>
                                                </div>
                                                <div className="wb-mastery-track">
                                                    <div
                                                        className="wb-mastery-fill"
                                                        style={{
                                                            width: `${word.mastery}%`,
                                                            background: word.mastery >= 70 ? '#1D9E75' :
                                                                word.mastery >= 40 ? '#EF9F27' : '#F09595'
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="wb-word-actions">
                                            <button className="wb-action-btn" onClick={() => setFlashcardMode(true)}>{s('flashcard')}</button>
                                            <button
                                                className="wb-action-btn primary"
                                                onClick={() => { setSentenceModalWord(word); setSentenceInput(''); setSentenceResult(null); }}
                                            >
                                                {s('useInSentence')}
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            <div style={{ height: '80px' }}></div>

            {showAddModal && (
                <div className="wb-modal-overlay" onClick={closeAddModal}>
                    <div className="wb-modal" onClick={e => e.stopPropagation()}>
                        {wordSuggestion ? (
                            <>
                                <div className="wb-modal-title">
                                    {wordSuggestion.relatedWord
                                        ? s('titleRelated')
                                        : wordSuggestion.isConjugated
                                            ? s('titleConjugated')
                                            : s('titleDidYouMean')}
                                </div>
                                <div className="wb-modal-desc">
                                    {wordSuggestion.relatedWord ? (
                                        wordSuggestion.relatedIsBase ? (
                                            <>{rich('descRelatedBase', { related: `"${wordSuggestion.relatedWord}"`, typed: newWord.trim() }, ['related'])}</>
                                        ) : (
                                            <>{rich('descRelatedForm', { related: `"${wordSuggestion.relatedWord}"`, base: wordSuggestion.wordToSave }, ['related'])}</>
                                        )
                                    ) : wordSuggestion.isConjugated ? (
                                        <>{rich('descConjugated', { typed: wordSuggestion.typedWord, info: wordSuggestion.conjugationInfo, baseForm: wordSuggestion.baseForm, translation: wordSuggestion.translation }, ['info', 'baseForm'])}</>
                                    ) : (
                                        <>{rich('descDidYouMean', { typed: newWord, suggestion: wordSuggestion.wordToSave, translation: wordSuggestion.translation }, ['suggestion'])}</>
                                    )}
                                </div>
                                <div className="wb-modal-actions" style={{ flexDirection: 'column', gap: 8 }}>
                                    {wordSuggestion.relatedWord ? (
                                        <>
                                            <button
                                                className="wb-modal-confirm"
                                                onClick={async () => {
                                                    const { data: { user } } = await supabase.auth.getUser();
                                                    if (!user) return;
                                                    const entryType = newWord.trim().split(/\s+/).length > 1 ? 'phrase' : 'word';
                                                    const note = `${wordSuggestion.conjugationInfo ? s('noteFormOf', { info: wordSuggestion.conjugationInfo, base: wordSuggestion.wordToSave }) : s('noteConjugatedOf', { base: wordSuggestion.wordToSave })} ${wordSuggestion.note || ''}`.trim();
                                                    await supabase.from('word_bank').upsert({
                                                        user_id: user.id,
                                                        word: newWord.trim(),
                                                        translation: wordSuggestion.translation,
                                                        phonetic: wordSuggestion.phonetic,
                                                        type: wordSuggestion.type,
                                                        note,
                                                        examples: wordSuggestion.examples || [],
                                                        entry_type: entryType,
                                                        source: 'manual',
                                                        original_input: newWord.trim(),
                                                        target_language: targetLang,
                                                    }, { onConflict: 'user_id,word,target_language', ignoreDuplicates: true });
                                                    closeAddModal();
                                                    setLoading(true);
                                                    await loadWords();
                                                }}
                                            >
                                                {s('yesSaveAnyway', { word: newWord.trim() })}
                                            </button>
                                            <button
                                                className="wb-modal-cancel"
                                                onClick={closeAddModal}
                                            >
                                                {s('noHaveIt')}
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <button
                                                className="wb-modal-confirm"
                                                onClick={() => saveEnrichedWord(wordSuggestion, newWord)}
                                            >
                                                {s('yesSave', { word: wordSuggestion.wordToSave })}
                                            </button>
                                            <button
                                                className="wb-modal-cancel"
                                                onClick={() => saveRawWord(newWord.trim())}
                                            >
                                                {s('noSaveAsTyped', { word: newWord })}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="wb-modal-title">{s('addTitle')}</div>
                                <div className="wb-modal-desc">
                                    {s('addDesc', { lang: langName })}
                                </div>
                                <input
                                    className="wb-modal-input"
                                    type="text"
                                    placeholder={s('addPlaceholder', { lang: langName })}
                                    value={newWord}
                                    onChange={e => setNewWord(e.target.value)}
                                    autoFocus
                                    onKeyDown={e => e.key === 'Enter' && checkAndAddWord()}
                                />
                                {wordError && <div className="wb-modal-error">{wordError}</div>}
                                <div className="wb-modal-actions">
                                    <button className="wb-modal-cancel" onClick={closeAddModal}>
                                        {s('cancel')}
                                    </button>
                                    <button
                                        className="wb-modal-confirm"
                                        disabled={!newWord.trim() || checkingWord}
                                        onClick={checkAndAddWord}
                                    >
                                        {checkingWord ? s('checking') : s('addBtn')}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {deleteConfirmId && (
                <div className="wb-modal-overlay" onClick={() => setDeleteConfirmId(null)}>
                    <div className="wb-modal" onClick={e => e.stopPropagation()}>
                        <div className="wb-modal-title">{s('deleteTitle')}</div>
                        <div className="wb-modal-desc">
                            {s('deleteDesc')}
                        </div>
                        <div className="wb-modal-actions">
                            <button className="wb-modal-cancel" onClick={() => setDeleteConfirmId(null)}>
                                {s('cancel')}
                            </button>
                            <button className="wb-modal-delete" onClick={confirmDeleteWord}>
                                {s('deleteBtn')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {sentenceModalWord && (
                <div className="wb-modal-overlay" onClick={() => setSentenceModalWord(null)}>
                    <div className="wb-modal" onClick={e => e.stopPropagation()}>
                        <div className="wb-modal-title">{s('sentenceTitle', { word: sentenceModalWord.word })}</div>
                        <div className="wb-modal-desc">
                            {s('sentenceDesc', { lang: langName, translation: sentenceModalWord.translation || s('thisWord') })}
                        </div>
                        <input
                            className="wb-modal-input"
                            type="text"
                            placeholder={s('sentencePlaceholder')}
                            value={sentenceInput}
                            onChange={e => setSentenceInput(e.target.value)}
                            autoFocus
                            onKeyDown={e => e.key === 'Enter' && !sentenceChecking && checkSentence()}
                        />
                        {sentenceResult && (
                            <div className={`wb-sentence-feedback ${sentenceResult.correct ? 'correct' : 'wrong'}`}>
                                <div className="wb-sentence-feedback-icon">{sentenceResult.correct ? '✓' : '✕'}</div>
                                <div>
                                    <div className="wb-sentence-feedback-text">{sentenceResult.feedback}</div>
                                    {sentenceResult.correctedSentence && (
                                        <div className="wb-sentence-corrected">{s('tryCorrected', { sentence: sentenceResult.correctedSentence })}</div>
                                    )}
                                </div>
                            </div>
                        )}
                        <div className="wb-modal-actions">
                            <button className="wb-modal-cancel" onClick={() => setSentenceModalWord(null)}>
                                {s('close')}
                            </button>
                            <button
                                className="wb-modal-confirm"
                                disabled={!sentenceInput.trim() || sentenceChecking}
                                onClick={checkSentence}
                            >
                                {sentenceChecking ? s('checking') : s('checkSentence')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {flashcardMode && (
                <FlashcardSession
                    words={filteredWords}
                    onAnswer={handleFlashcardAnswer}
                    onClose={() => setFlashcardMode(false)}
                />
            )}

            <div className="wb-bottom-nav">
                <button className="wb-nav-item" onClick={() => navigate('/home')}>
                    <div className="wb-nav-icon">🏠</div>
                    <div className="wb-nav-label">{s('navHome')}</div>
                </button>
                <button className="wb-nav-item" onClick={() => navigate('/topics')}>
                    <div className="wb-nav-icon">📚</div>
                    <div className="wb-nav-label">{s('navTopics')}</div>
                </button>
                <button className="wb-nav-item" onClick={() => navigate('/grammar')}>
                    <div className="wb-nav-icon nav-icon-grammar">📐</div>
                    <div className="wb-nav-label">{s('navGrammar')}</div>
                </button>
                <button className="wb-nav-item active">
                    <div className="wb-nav-icon">📒</div>
                    <div className="wb-nav-label">{s('navWordBank')}</div>
                    <div className="wb-nav-dot"></div>
                </button>
                <button className="wb-nav-item" onClick={() => navigate('/games')}>
                    <div className="wb-nav-icon">🎮</div>
                    <div className="wb-nav-label">{s('navGames')}</div>
                </button>
            </div>

        </div>
    );
}

export default WordBank;